import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EMPTY, forkJoin } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TestSuitesActions } from './testSuites.actions';
import { TestSuitesStateModel } from './testSuites.state.model';
import { patch, removeItem } from '@ngxs/store/operators';
import { TestSuiteApiService } from 'src/app/services/api/test-suite-api.service';
import { BaseTestItemDto } from 'src/app/models/dto/Base-Test-item-dto';

@State<TestSuitesStateModel>({
  name: 'testSuites',
  defaults: {
    testSuites: [],
    orgGroupTestSuites: [],
    functionalGroupTestSuites: [],
    myTestSuites: []
  },
})
@Injectable()
export class TestSuitesState {
  pageNumber = 1;
  pageSize = 10;
  constructor(private testSuiteApiService: TestSuiteApiService) {}

  @Action(TestSuitesActions.SetAllTestSuites)
  SetAllTestSuites(ctx: StateContext<TestSuitesStateModel>, action :TestSuitesActions.SetAllTestSuites) {
    const state = ctx.getState();
    //don't update state if we have already downloaded items
    if (state.testSuites !== undefined && state.testSuites.length > 0)
      return EMPTY;

    return this.testSuiteApiService.getAll(action.company, '', '').pipe(
      tap(testSuites =>{
        ctx.patchState({ testSuites: testSuites });
      })
    );
  }

  @Action(TestSuitesActions.SetOrgGroupTestSuites)
  SetOrgGroupTestSuites(ctx: StateContext<TestSuitesStateModel>, action: TestSuitesActions.SetOrgGroupTestSuites) {
    const state = ctx.getState();
    //don't update state if we have already downloaded items
    if (state.orgGroupTestSuites !== undefined && state.orgGroupTestSuites.length > 0)
      return EMPTY;

    return this.testSuiteApiService.getAll('', '', action.orgGroupId ).pipe(
      tap(testSuites =>{
        ctx.patchState({ orgGroupTestSuites: testSuites });
      })
    );
  }

  @Action(TestSuitesActions.SetMyTestSuites)
  SetMyTestSuites(ctx: StateContext<TestSuitesStateModel>, action: TestSuitesActions.SetMyTestSuites) {
    const state = ctx.getState();
    //don't update state if we have already downloaded items
    if (state.myTestSuites !== undefined && state.myTestSuites.length > 0)
      return EMPTY;

    return this.testSuiteApiService.getAll('', action.userId, '' ).pipe(
      tap(testSuites =>{
        ctx.patchState({ myTestSuites: testSuites });
      })
    );
  }

  @Action(TestSuitesActions.SetFunctionalGroupTestSuites)
  SetFunctionalGroupTestSuites(ctx: StateContext<TestSuitesStateModel>, action: TestSuitesActions.SetFunctionalGroupTestSuites) {
    const state = ctx.getState();
    //don't update state if we have already downloaded items
    if (state.functionalGroupTestSuites !== undefined && state.functionalGroupTestSuites.length > 0)
      return EMPTY;

    // The group id goes in the third (OrgGroup) slot. It used to land in regBy, which the
    // old API matched against the creator (so: nothing) and the Go path turned into scope
    // MINE (so: the caller's own items). Results are merged and de-duplicated — each
    // response used to overwrite the previous one. On the Go path every call resolves to
    // scope MY_GROUP, i.e. items owned by ANY of the caller's groups, so the merge is that set.
    const observables = action.functionalGroupIds.map(fg => this.testSuiteApiService.getAll('', '', fg));
    if (observables.length === 0) return EMPTY;
    return forkJoin(observables).pipe(
      tap(results => {
        const byUid = new Map<string, BaseTestItemDto>();
        for (const list of results) {
          for (const item of list ?? []) {
            if (item?.uid) byUid.set(item.uid, item);
          }
        }
        ctx.patchState({ functionalGroupTestSuites: [...byUid.values()] });
      })
    );
  }

  @Action(TestSuitesActions.DeleteTestSuite)
  DeleteTestSuite(ctx: StateContext<TestSuitesStateModel>, action: TestSuitesActions.DeleteTestSuite){
    return this.testSuiteApiService.delete(action.testSuiteUid, action.regBy).pipe(
      tap(result =>{
        ctx.setState(
          patch<TestSuitesStateModel>({
            orgGroupTestSuites: removeItem<BaseTestItemDto>(x => x.uid === action.testSuiteUid),
            testSuites: removeItem<BaseTestItemDto>(x => x.uid === action.testSuiteUid)
          })
        )
      })
    );
  }

  @Action(TestSuitesActions.UpdateItem)
  updateItem(ctx: StateContext<TestSuitesStateModel>, action: TestSuitesActions.UpdateItem) {
    const state = ctx.getState();

    // Update in testSuites (All)
    const allIdx = state.testSuites.findIndex(item => item.uid === action.item.uid);
    if (allIdx >= 0) {
      const updated = [...state.testSuites];
      updated[allIdx] = action.item;  // Full replace
      ctx.patchState({ testSuites: updated });
    }

    // Update in orgGroupTestSuites
    const orgIdx = state.orgGroupTestSuites.findIndex(item => item.uid === action.item.uid);
    if (orgIdx >= 0) {
      const updated = [...state.orgGroupTestSuites];
      updated[orgIdx] = action.item;
      ctx.patchState({ orgGroupTestSuites: updated });
    }

    // Update in myTestSuites
    const myIdx = state.myTestSuites.findIndex(item => item.uid === action.item.uid);
    if (myIdx >= 0) {
      const updated = [...state.myTestSuites];
      updated[myIdx] = action.item;
      ctx.patchState({ myTestSuites: updated });
    }

    // Update in functionalGroupTestSuites
    const fgIdx = state.functionalGroupTestSuites.findIndex(item => item.uid === action.item.uid);
    if (fgIdx >= 0) {
      const updated = [...state.functionalGroupTestSuites];
      updated[fgIdx] = action.item;
      ctx.patchState({ functionalGroupTestSuites: updated });
    }
  }

  @Action(TestSuitesActions.ClearCache)
  clearCache(ctx: StateContext<TestSuitesStateModel>) {
    ctx.patchState({
      testSuites: [],
      orgGroupTestSuites: [],
      functionalGroupTestSuites: [],
      myTestSuites: []
    });
  }

  @Selector()
  static getAllTestSuites(state: TestSuitesStateModel) {
    return state.testSuites;
  }

  @Selector()
  static getOrgGroupTestSuites(state: TestSuitesStateModel) {
    return state.orgGroupTestSuites;
  }

  @Selector()
  static getMyTestSuites(state: TestSuitesStateModel) {
    return state.myTestSuites;
  }

  @Selector()
  static getFgTestSuites(state: TestSuitesStateModel) {
    return state.functionalGroupTestSuites;
  }
}
